import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, ClipboardList, CreditCard, SearchCheck, ShieldCheck, MapPin, Users, Award } from "lucide-react";
import { api } from "@/context/AuthContext";
import { Kicker, MaskedLine, Reveal } from "@/components/Motion";
import ServiceCard from "@/components/ServiceCard";
import { SERVICES } from "@/data/services";

const HERO_IMG =
  "https://images.unsplash.com/photo-1560493676-04071c5f467b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxhZ3JpY3VsdHVyZSUyMGZhcm1pbmclMjBmaWVsZHxlbnwwfHx8fDE3ODcwMTc2Nzd8MA&ixlib=rb-4.1.0&q=85";

const STEPS = [
  { icon: SearchCheck, title: "Choose a service", text: "Browse 13 service categories and open the one you need to see eligibility, documents and fees." },
  { icon: ClipboardList, title: "Book online", text: "Fill a short booking form with your details and preferred date. It takes under two minutes." },
  { icon: CreditCard, title: "Pay the fee", text: "Where a fee applies, pay securely online via Razorpay — or at the service desk." },
  { icon: CheckCircle2, title: "Track & attend", text: "Track booking and payment status anytime from your account. Our team confirms your slot." },
];

const WHY = [
  { icon: ShieldCheck, title: "Trusted & transparent", text: "Clear eligibility, listed documents and published fees — no hidden charges, no middlemen." },
  { icon: Users, title: "Local trained teams", text: "Our operators and advisors come from the communities they serve and speak your language." },
  { icon: Award, title: "Certified programs", text: "Training batches include completion certificates and guidance toward real livelihoods." },
  { icon: MapPin, title: "Camps near you", text: "Regular Saturday camps and monthly health drives bring services closer to your doorstep." },
];

export default function Home() {
  const [notices, setNotices] = useState([]);

  useEffect(() => {
    api
      .get("/notices")
      .then((r) => setNotices(r.data.slice(0, 3)))
      .catch(() => setNotices([]));
  }, []);

  return (
    <div data-testid="home-page">
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:gap-8 lg:px-8 lg:py-24">
          <div className="lg:col-span-6">
            <Reveal delay={0.05} y={10}>
              <Kicker>National Union Corporation</Kicker>
            </Reveal>
            <h1 className="mt-4 font-display font-bold tracking-tight text-slate-900">
              <MaskedLine delay={0.12} className="text-4xl sm:text-5xl lg:text-6xl">
                NUC Service
              </MaskedLine>
              <MaskedLine delay={0.24} className="text-2xl text-blue-900 sm:text-3xl lg:text-4xl">
                National Union Corporation
              </MaskedLine>
            </h1>
            <Reveal delay={0.4} className="mt-6 max-w-xl">
              <p className="text-base leading-relaxed text-slate-700 sm:text-lg">
                Supporting Digital Services, Agriculture, Rural Development, Employment, Skills
                and Community Development.
              </p>
            </Reveal>
            <Reveal delay={0.55} className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/services"
                data-testid="hero-explore-button"
                className="inline-flex items-center gap-2 rounded-md bg-blue-900 px-6 py-3 text-sm font-medium text-white transition-colors duration-200 hover:bg-blue-800"
              >
                Explore Services
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link
                to="/book"
                data-testid="hero-book-button"
                className="inline-flex items-center gap-2 rounded-md border-2 border-slate-300 px-6 py-3 text-sm font-medium text-slate-700 transition-colors duration-200 hover:border-blue-900 hover:text-blue-900"
              >
                Book a Service
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.3}>
              <div className="relative overflow-hidden rounded-xl border border-slate-200 shadow-sm">
                <img
                  src={HERO_IMG}
                  alt="Farmers working in a green field at sunrise"
                  className="max-h-[60vh] w-full object-cover lg:max-h-[480px]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" aria-hidden="true" />
                <p className="absolute bottom-4 left-4 right-4 font-mono text-[11px] uppercase tracking-[0.15em] text-white">
                  Field programs across agriculture, skills and digital literacy
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200" data-testid="home-about-section">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:px-8 lg:py-24">
          <Reveal className="lg:col-span-5">
            <Kicker>About NUC Service</Kicker>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              A service platform built for communities
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <p className="text-base leading-relaxed text-slate-700">
              NUC Service (National Union Corporation) connects citizens with the services that
              matter — digital and CSC support, agricultural guidance, skill training, employment
              linkage, welfare programs and institutional collaboration. We work at the last mile,
              in plain language, with published processes and honest fees.
            </p>
            <Link
              to="/about"
              data-testid="home-about-link"
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-blue-900 transition-colors duration-200 hover:text-blue-700"
            >
              Learn more about us
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-white" data-testid="home-services-section">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Kicker>Our Services</Kicker>
              <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
                13 categories. One platform.
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="home-all-services-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-blue-900 transition-colors duration-200 hover:text-blue-700"
            >
              View all services
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.slice(0, 6).map((s, i) => (
              <Reveal key={s.slug} delay={(i % 3) * 0.06}>
                <ServiceCard service={s} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200" data-testid="home-how-it-works">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <Reveal>
            <Kicker>How It Works</Kicker>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              From booking to service in four steps
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((step, i) => (
              <Reveal key={step.title} delay={i * 0.07}>
                <div className="h-full rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="flex items-center justify-between">
                    <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-blue-50 text-blue-900">
                      <step.icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span className="font-mono text-sm font-semibold text-slate-400">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="mt-5 font-display text-lg font-semibold tracking-tight text-slate-900">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-white" data-testid="home-why-section">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <Reveal>
            <Kicker>Why Choose NUC Service</Kicker>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              Built on trust, measured in outcomes
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.07}>
                <div className="h-full rounded-xl border border-slate-200 bg-slate-50 p-6">
                  <w.icon className="h-6 w-6 text-green-600" aria-hidden="true" />
                  <h3 className="mt-4 font-display text-lg font-semibold tracking-tight text-slate-900">
                    {w.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200" data-testid="home-notices-section">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Kicker>Latest Notices</Kicker>
              <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
                News and announcements
              </h2>
            </div>
            <Link
              to="/notices"
              data-testid="home-all-notices-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-blue-900 transition-colors duration-200 hover:text-blue-700"
            >
              All notices
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
          <div className="mt-10 space-y-4">
            {notices.map((n, i) => (
              <Reveal key={n.id} delay={i * 0.05}>
                <Link
                  to="/notices"
                  data-testid={`home-notice-${n.id}`}
                  className={`block rounded-xl border bg-white p-5 shadow-sm transition-colors duration-200 hover:border-blue-200 ${
                    n.important ? "border-l-4 border-l-amber-500 border-slate-200" : "border-slate-200"
                  }`}
                >
                  <div className="flex flex-wrap items-center gap-3">
                    {n.important && (
                      <span className="rounded-full bg-amber-100 px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-amber-800">
                        Important
                      </span>
                    )}
                    <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">
                      {n.date}
                    </span>
                  </div>
                  <h3 className="mt-2 font-display text-lg font-semibold tracking-tight text-slate-900">
                    {n.title}
                  </h3>
                </Link>
              </Reveal>
            ))}
            {notices.length === 0 && (
              <p className="text-sm text-slate-500">No notices at the moment.</p>
            )}
          </div>
        </div>
      </section>

      <section className="bg-blue-900" data-testid="home-cta-section">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              Need help with a service today?
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-blue-100">
              Book online in two minutes or talk to our support team — we will guide you to the
              right desk, documents and scheme.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/book"
                data-testid="home-cta-book-button"
                className="rounded-md bg-white px-6 py-3 text-sm font-semibold text-blue-900 transition-colors duration-200 hover:bg-blue-50"
              >
                Book a Service
              </Link>
              <Link
                to="/contact"
                data-testid="home-cta-contact-button"
                className="rounded-md border-2 border-white/40 px-6 py-3 text-sm font-medium text-white transition-colors duration-200 hover:border-white"
              >
                Contact Support
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
